<template>
  <div class="common-node-options">
    <!-- NEW NODES -->
    <not-allowed-tooltip
      class="d-flex"
      :user-permissions="userPermissions"
      permission-key="canCreateContent"
    >
      <template v-slot:main-content="{ hasPermissions }">
        <AddNodeOptions
          v-if="!nodeType"
          class="mr-1"
          :parent-ids="parentIds"
          :can-create-content="hasPermissions"
          @add-child="$emit('add-child', $event)"
          @add-child-from-template="$emit('add-child-from-template')"
        />
      </template>
    </not-allowed-tooltip>

    <!-- NODE EDITION -->
    <EditNodeOptions
      :type="nodeType"
      :is-outdated="isOutdated"
      :user-permissions="userPermissions"
      @edit-label="$emit('edit-label')"
      @update-content="$emit('update-content', $event)"
      @update-content-verification="
        $emit('update-content-verification', $event)
      "
      @clone-kb="handleOpenClone"
      @duplicate-in-same-location="handleDuplicateInSameLocation"
      @delete-node="handleDeleteNode"
    />

    <ModalEvents
      modal-name="DeleteContentModal"
      :uid="deleteModalUniqueKey"
      @choice="deleteNode"
    />
    <CloneKnowledgeBaseModal
      v-if="display.cloneTo"
      :display="display.cloneTo"
      :sourceType="sourceToClone.type"
      :sourceLabel="sourceToClone.label"
      :sourceId="sourceToClone.sourceId"
      @close="display.cloneTo = false"
      @clone-to-target="cloneTo"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import AddNodeOptions from './AddNodeOptions';
import EditNodeOptions from './EditNodeOptions';
import CloneKnowledgeBaseModal from 'components/Modals/CloneKnowledgeBaseModal/CloneKnowledgeBaseModal';
import ModalEvents from '@/components/Modals/ModalEvents';
import NotAllowedTooltip from '@/components/Commons/NotAllowedTooltip';

export default {
  name: 'common-node-options',
  components: {
    AddNodeOptions,
    EditNodeOptions,
    CloneKnowledgeBaseModal,
    ModalEvents,
    NotAllowedTooltip,
  },
  props: {
    label: String,
    nodeId: String,
    nodeType: String,
    parentIds: Array,
    isOutdated: Boolean,
    userPermissions: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    deleteModalUniqueKey() {
      return `${this.$options.name}-${this.nodeId}`;
    },
    cloneType() {
      return !this.nodeType ? 'case' : 'contents';
    },
    ...mapGetters('knowledgeModule', [
      'focusKnowledgeId',
      'focusKnowledgeValue',
    ]),
  },
  data() {
    return {
      display: {
        delete: false,
        cloneTo: false,
      },
      sourceType: '',
      sourceToClone: {
        type: '',
        label: '',
        sourceId: null,
      },
    };
  },
  methods: {
    handleDuplicateInSameLocation() {
      this.sourceToClone = { type: this.cloneType };

      const clonePayload = {
        targetCaseId: this.parentIds.length > 2 ? this.parentIds.at(-2) : null, // Direct parent of cloned node or null if it is a rootCase
        targetKnowledgeId: this.focusKnowledgeId,
        targetKnowledge: this.focusKnowledgeValue,
      };

      if (this.cloneType === 'case') {
        clonePayload.caseName = this.label;
      }

      this.cloneTo(clonePayload);
    },
    handleOpenClone() {
      this.sourceType = this.cloneType;
      if (!this.nodeType) {
        // Node is a Case
        this.sourceToClone = {
          type: 'case',
          label: this.label,
          sourceId: this.nodeId,
        };
      } else {
        this.sourceToClone = {
          type: 'contents',
          label: this.$tc('knowledge.modals.clone-kb.contents-count'),
        };
      }
      this.display.cloneTo = true;
    },
    closeCloneModal() {
      this.display.cloneTo = false;
    },
    cloneTo(payload) {
      const payloadToSend = {
        ...payload,
      };

      if (!this.nodeType) {
        payloadToSend.caseId = this.nodeId;
      } else {
        payloadToSend.contentIds = [this.nodeId];
      }

      this.$emit('clone-to-target', {
        payload: payloadToSend,
        type: this.sourceToClone.type,
        closeCallback: this.closeCloneModal,
      });
    },
    async handleDeleteNode() {
      let props = {
        display: true,
      };
      // CASE DELETE
      if (!this.nodeType) {
        let loadingMessage = this.$message({
          duration: 0,
          dangerouslyUseHTMLString: true,
          iconClass: 'display: none',
          message: `<i class="fas fa-spinner fa-spin" style="margin-right: 1em;"></i> ${this.$t(
            'components.modals.delete-modal.loading',
          )}`,
        });
        const count =
          await this.$services.parametricContents.filterContentsCount(
            undefined,
            [],
            [this.nodeId],
            this.focusKnowledgeId,
          );
        loadingMessage.close();
        props.isCase = true;
        props.caseCount = count;
      } else {
        const contentId = this.nodeId;
        const relatedCount = await this.countRelatedContentReferences({
          contentId,
          knowledgeId: this.focusKnowledgeId,
        });
        const backlinks = await this.getContentBacklinks(contentId);
        props.id = contentId;
        props.relatedCount = relatedCount;
        props.backlinks = backlinks;
      }
      const payload = {
        component: 'DeleteContentModal',
        uid: this.deleteModalUniqueKey,
        props,
      };
      this.openModal(payload);
    },
    deleteNode(choice) {
      if (choice) this.$emit('delete-node');
    },
    ...mapActions('modalsModule', ['openModal']),
    ...mapActions('knowledgeModule', [
      'countRelatedContentReferences',
      'getContentBacklinks',
    ]),
  },
};
</script>
